/**
 * Admin-side time display helpers.
 *
 * Everything in the DB is stored in UTC (ISO strings). Jamaica runs on
 * UTC-5 year-round with no daylight savings, so the conversion is a flat
 * subtract-five-hours operation — we use `America/Jamaica` via Intl instead
 * of hardcoding the offset, so if PayPal/Resend schedulers ever care about
 * DST the code stays correct.
 *
 * All formatters defensively no-op on null/invalid inputs so a single bad
 * row doesn't break a whole admin table.
 */

const JA_TZ = "America/Jamaica";

/** Safely build a Date from an ISO string; returns null when invalid. */
function parseIso(value) {
  if (!value || typeof value !== "string") return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * "YYYY-MM-DD h:MM AM/PM JA" — compact, sortable date with 12-hour time.
 * Used in detail tables where space is at a premium but users still want
 * human-readable AM/PM instead of 24-hour "army time".
 */
export function formatJamaicaCompact(isoString) {
  const d = parseIso(isoString);
  if (!d) return "—";
  // Date portion via en-CA for guaranteed YYYY-MM-DD ordering.
  const dateParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: JA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const getDate = (type) => dateParts.find((p) => p.type === type)?.value || "";
  // Time portion via en-US for h:MM AM/PM.
  const time = new Intl.DateTimeFormat("en-US", {
    timeZone: JA_TZ,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
  return `${getDate("year")}-${getDate("month")}-${getDate("day")} ${time} JA`;
}

/**
 * Time-only, 12-hour with AM/PM, e.g. "2:30 PM". No "JA" suffix because this
 * is used in-flow ("report again at 2:30 PM") where the zone is implied.
 */
export function formatJamaicaTime(isoString) {
  const d = parseIso(isoString);
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: JA_TZ,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}

/**
 * Human-friendly long form, e.g. "Jan 1, 2026, 2:00 PM JA".
 * Used in PDF headers and sparse displays where readability matters more
 * than compactness.
 */
export function formatJamaicaLong(isoString) {
  const d = parseIso(isoString);
  if (!d) return "—";
  const formatted = new Intl.DateTimeFormat("en-US", {
    timeZone: JA_TZ,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
  return `${formatted} JA`;
}

/**
 * Date-only in JA, e.g. "Jan 1, 2026". Used where the time portion would be
 * noise (subscription renewal date, expiry date, submission created_at badge).
 */
export function formatJamaicaDate(isoString) {
  const d = parseIso(isoString);
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: JA_TZ,
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(d);
}

/** ISO-looking output in JA wall-clock time (useful for CSV where consumers
 *  want a machine-readable but human-context timestamp). */
export function formatJamaicaIso(isoString) {
  const d = parseIso(isoString);
  if (!d) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: JA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")} JA`;
}
